import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DisplayData } from '@/components/dsr-analytics/types';
import { Text, View } from 'react-native';

interface Column {
  key: string;
  render?: (value: string | number) => React.ReactNode;
  title: string;
}

interface Props {
  columns: Column[];
  data: DisplayData[];
}

export const AlertTable: React.FC<Props> = ({ data, columns }) => {
  const [styles] = useStyles();

  const renderCell = (item: DisplayData, column: Column) => {
    const value = item[column.key];
    if (column.render) {
      return column.render(value);
    }
    return String(value);
  };

  const getColumnStyle = (index: number) => {
    if (index === 0) {
      return styles.leftAlignedColumn;
    }
    if (index === columns.length - 1) {
      return styles.rightAlignedColumn;
    }
    return styles.centerAlignedColumn;
  };

  const getTextStyle = (index: number) => {
    if (index === columns.length - 1) {
      return styles.rightAlignedText;
    }
    return styles.leftAlignedText;
  };

  const renderRow = (item: DisplayData, rowIndex: number) => (
    <View key={rowIndex} style={styles.row}>
      {columns.map((column, colIndex) => (
        <View
          key={colIndex}
          style={[styles.cellContainer, getColumnStyle(colIndex)]}
        >
          <View style={styles.cellContent}>
            <Text style={[styles.cell, getTextStyle(colIndex)]}>
              {renderCell(item, column)}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );

  return (
    <View style={styles.base}>
      <View style={styles.row}>
        {columns.map((column, colIndex) => (
          <View
            key={colIndex}
            style={[styles.cellContainer, getColumnStyle(colIndex)]}
          >
            <View style={styles.cellContent}>
              <Text style={[styles.headerCell, getTextStyle(colIndex)]}>
                {column.title}
              </Text>
            </View>
          </View>
        ))}
      </View>
      {data.map((item, rowIndex) => renderRow(item, rowIndex))}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'column',
    paddingVertical: 8,
  },
  cell: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: theme.fontSizes.medium,
  },
  cellContainer: {
    flex: 1,
  },
  cellContent: {
    flex: 1,
    justifyContent: 'center',
  },
  centerAlignedColumn: {
    alignItems: 'center',
  },
  headerCell: {
    color: theme.colors.primary,
    fontFamily: theme.fonts.primarySemiBold,
    fontSize: theme.fontSizes.small,
    textTransform: 'uppercase',
  },
  leftAlignedColumn: {
    alignItems: 'flex-start',
  },
  leftAlignedText: {
    textAlign: 'left',
  },
  rightAlignedColumn: {
    alignItems: 'flex-end',
  },
  rightAlignedText: {
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    // avoid scroll bar
    paddingRight: 8,
    paddingVertical: 8,
  },
}));
